'use client';
import React, { useMemo, useEffect, useCallback, useRef, useState } from 'react';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
} from '@ui/components';
import { ContextUserType, IMessage, useMessageContext } from '@ui/components/organisms/message';
import { Button } from '@ui/components/ui/button';
import { Ellipsis, Flag, PencilOff, Trash, PinIcon, Share2 } from 'lucide-react';
import { PostItemDeleteActionModal } from './post-item-delete-action-modal';
import { useReportContent } from '@ui/contexts';
import { useInteractionGuard } from '@hooks/index';
import { ShareModal } from '@ui/components/organisms/share-modal';
import { DynamicRouteBuilder } from '@ui/constants';
import { usePostHogFeatureFlag } from '@ui/hooks/posthog/usePostHogFeatureFlag';

interface Props {
  post: IMessage;
  userType: ContextUserType;
  userId: string;
  enableContentFlagging: boolean;
  reportLabel?: string;

  dropdownOpen: boolean;
  contentType?: string;
  isReply?: boolean;
  communityDetails: NonNullable<IMessage['community_details']>;
  moderators_ids: string[];
  onUpdateTrigger: () => void;
  setDropdownOpen: (open: boolean) => void;
}

export const PostItemsMoreOptions: React.FC<Props> = ({
  post,
  userType,
  userId,
  enableContentFlagging,
  reportLabel,
  onUpdateTrigger,
  dropdownOpen,
  setDropdownOpen,
  contentType,
  isReply,
  communityDetails,
  moderators_ids,
}) => {
  const enableURLShareModal = usePostHogFeatureFlag('enable-url-share-modal');
  const dropDownContentRef = useRef<HTMLDivElement>(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const messageState = useMessageContext(userType);
  const { openReportContent, onReasonSelected } = useReportContent();
  const { interceptInteraction } = useInteractionGuard();

  const joinMessage = useMemo(
    () =>
      communityDetails?.creator_display_name
        ? `Join community to use this feature. Collect this badge to join community!`
        : 'Join this KOR Space to use this feature. Collect this badge to join.',
    [communityDetails?.creator_display_name],
  );

  const onDeleteOpen = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDeleteModalOpen(true);
  }, []);

  useEffect(() => {
    const handleReasonSelected = (reason: string, message: IMessage) =>
      messageState.onReportMessage({ message_id: message.id, report: reason });
    onReasonSelected(handleReasonSelected);
  }, [onReasonSelected, messageState]);

  const onEdit = interceptInteraction((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onUpdateTrigger();
    setDropdownOpen(false);
  }, joinMessage);

  const canEdit = useMemo(
    () => post.user_id === userId && !post.block,
    [post.user_id, userId, post.block],
  );

  const isModerator = useMemo(
    () => moderators_ids?.includes(userId) || false,
    [moderators_ids, userId],
  );

  const isCommunityOwner = useMemo(
    () => userId === communityDetails.creator_id,
    [userId, communityDetails.creator_id],
  );
  const isBaseCommunity = useMemo(
    () => communityDetails.conversation_type === 'community',
    [communityDetails.conversation_type],
  );
  const isPostOwner = useMemo(() => userId === post.user_id, [userId, post.user_id]);

  const canDelete = useMemo(() => {
    if (isPostOwner) {
      return true;
    }

    if (isCommunityOwner) {
      return true;
    }

    if (isModerator && !isBaseCommunity) {
      return true;
    }
    return false;
  }, [isCommunityOwner, isModerator, isBaseCommunity, isPostOwner]);

  const showDropdownTrigger = useMemo(() => {
    return enableContentFlagging || userId === post.user_id || isModerator;
  }, [enableContentFlagging, userId, post.user_id, isModerator]);

  const showOnlyEditDelete = useMemo(() => post.draft, [post.draft]);

  const onPin = interceptInteraction((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDropdownOpen(false);
    messageState.onPinMessage(
      post.id,
      post.pinned_at ? false : true,
      userId,
      post.thread_id,
      post.conversation_id,
      contentType ? 'Post' : 'Comment',
    );
  }, joinMessage);

  const onDelete = interceptInteraction(onDeleteOpen, joinMessage);
  const onReport = interceptInteraction(() => openReportContent(post), joinMessage);

  return (
    <>
      <PostItemDeleteActionModal
        open={deleteModalOpen}
        setOpen={setDeleteModalOpen}
        post={post}
        userType={userType}
        isReply={isReply}
        isModerator={isModerator}
      />

      {(enableURLShareModal || showDropdownTrigger) && (
        <DropdownMenu open={dropdownOpen} onOpenChange={setDropdownOpen}>
          <DropdownMenuTrigger asChild>
            <Button size="icon" variant="ghost" className="rounded">
              <Ellipsis className="h-4 w-4 " />
            </Button>
          </DropdownMenuTrigger>

          <DropdownMenuContent className="w-40" hideWhenDetached ref={dropDownContentRef}>
            <DropdownMenuGroup className="w-40">
              {!showOnlyEditDelete && contentType === 'post' && enableURLShareModal && (
                <DropdownMenuItem
                  className="space-x-2 w-full"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}>
                  <ShareModal
                    url={`${process.env.NEXT_PUBLIC_FAN_WEB_URL}${DynamicRouteBuilder.postBySlug(
                      communityDetails.creator_display_name || communityDetails.creator_id,
                      post.conversation_id,
                      post.slug,
                    )}`}
                    title={`Check out ${communityDetails.creator_display_name}'s post on Kor`}>
                    <div className="flex items-center space-x-2 w-full min-w-full">
                      <Share2 className="h-4 w-4" />
                      <span>Share</span>
                    </div>
                  </ShareModal>
                </DropdownMenuItem>
              )}

              {showDropdownTrigger && (
                <>
                  {!showOnlyEditDelete &&
                    canEdit &&
                    userType === 'creator' &&
                    post.created_by_creator && (
                      <DropdownMenuItem className="space-x-2" onClick={onPin}>
                        <PinIcon className="h-4 w-4" />
                        {post.pinned_at ? <span>Unpin</span> : <span>Pin</span>}
                      </DropdownMenuItem>
                    )}

                  {canEdit && (
                    <DropdownMenuItem className="space-x-2" onClick={onEdit}>
                      <PencilOff className="h-4 w-4" />
                      <span>Edit</span>
                    </DropdownMenuItem>
                  )}

                  {canDelete && (
                    <DropdownMenuItem className="space-x-2" onClick={onDelete}>
                      <Trash className="h-4 w-4" />
                      <span>Delete</span>
                    </DropdownMenuItem>
                  )}

                  {!showOnlyEditDelete && enableContentFlagging && (
                    <DropdownMenuItem className="space-x-2" onClick={onReport}>
                      <Flag className="h-4 w-4" />
                      <span>{reportLabel}</span>
                    </DropdownMenuItem>
                  )}
                </>
              )}
            </DropdownMenuGroup>
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </>
  );
};
